import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import {
  Search, MoreHorizontal, RotateCcw, Ban, FileText, Download,
} from "lucide-react";

const STATUS_STYLES = {
  completed: "bg-brand-green/10 text-brand-green border-brand-green/20",
  printing: "bg-brand-blue/10 text-brand-blue border-brand-blue/20",
  queued: "bg-amber-500/10 text-amber-600 border-amber-500/20",
  cancelled: "bg-destructive/10 text-destructive border-destructive/20",
};

export default function Orders() {
  const { user, refreshMe } = useAuth();
  const [orders, setOrders] = useState([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (search) params.append("search", search);
    if (status !== "all") params.append("status", status);
    const { data } = await api.get(`/orders?${params.toString()}`);
    setOrders(data);
    setLoading(false);
  };

  useEffect(() => { load(); }, [status]); // eslint-disable-line react-hooks/exhaustive-deps

  const money = (v) => "₹" + Number(v || 0).toFixed(2);

  const reprint = async (id) => {
    try {
      await api.post(`/orders/${id}/reprint`);
      toast.success("Reprint sent!");
      await refreshMe();
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };
  const cancel = async (id) => {
    try {
      await api.post(`/orders/${id}/cancel`, { reason: "user-cancel" });
      toast.success("Order cancelled and refunded");
      await refreshMe();
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };
  const downloadCSV = () => {
    const headers = ["Order ID", "File", "Pages", "Copies", "Color", "Paper", "Status", "Cost", "Created"];
    const rows = orders.map((o) => [
      o.id, o.filename, o.total_pages, o.copies, o.color ? "Color" : "B&W",
      o.paper_size, o.status, o.cost, o.created_at,
    ]);
    const csv = [headers, ...rows].map((r) => r.map((c) => `"${c}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `maaprint-orders-${Date.now()}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6" data-testid="orders-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-widest text-muted-foreground">Print history</p>
          <h1 className="text-3xl font-display font-semibold tracking-tight mt-1">Orders</h1>
        </div>
        <div className="flex flex-wrap gap-2">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search filename…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
              className="pl-9 w-56"
              data-testid="orders-search-input"
            />
          </div>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-40" data-testid="orders-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="printing">Printing</SelectItem>
              <SelectItem value="queued">Queued</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={downloadCSV} data-testid="orders-download-btn">
            <Download size={16} className="mr-2" /> Export CSV
          </Button>
        </div>
      </div>

      <Card className="border-border bg-card overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>File</TableHead>
              {user.role === "super_admin" && <TableHead>User</TableHead>}
              <TableHead className="text-center">Pages × Copies</TableHead>
              <TableHead>Mode</TableHead>
              <TableHead>Printer</TableHead>
              <TableHead className="text-right">Cost</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-12"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={8} className="text-center py-12 text-muted-foreground">Loading…</TableCell></TableRow>
            ) : orders.length === 0 ? (
              <TableRow><TableCell colSpan={8} className="text-center py-12 text-muted-foreground" data-testid="orders-empty">No orders found.</TableCell></TableRow>
            ) : orders.map((o) => (
              <TableRow key={o.id} data-testid={`order-row-${o.id}`}>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <FileText size={14} className="text-primary flex-shrink-0" />
                    <span className="font-medium max-w-[220px] truncate">{o.filename}</span>
                  </div>
                  <p className="text-[11px] text-muted-foreground">
                    {new Date(o.created_at).toLocaleString()}
                  </p>
                </TableCell>
                {user.role === "super_admin" && <TableCell className="text-xs">{o.user_name}</TableCell>}
                <TableCell className="text-center tabular-nums text-sm">
                  {o.total_pages} × {o.copies}
                </TableCell>
                <TableCell>
                  <Badge variant="outline" className="text-xs">
                    {o.color ? "Color" : "B&W"}{o.duplex ? " · Duplex" : ""} · {o.paper_size}
                  </Badge>
                </TableCell>
                <TableCell className="text-xs">{o.printer_name || "—"}</TableCell>
                <TableCell className="text-right tabular-nums font-medium">{money(o.cost)}</TableCell>
                <TableCell>
                  <Badge variant="outline" className={STATUS_STYLES[o.status]}>
                    {o.status}
                  </Badge>
                </TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" data-testid={`order-actions-${o.id}`}>
                        <MoreHorizontal size={16} />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => reprint(o.id)} data-testid={`order-reprint-${o.id}`}>
                        <RotateCcw size={14} className="mr-2" /> Reprint
                      </DropdownMenuItem>
                      {o.status !== "cancelled" && o.status !== "completed" && (
                        <DropdownMenuItem onClick={() => cancel(o.id)} className="text-destructive" data-testid={`order-cancel-${o.id}`}>
                          <Ban size={14} className="mr-2" /> Cancel
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
